import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Mail, User, MessageSquare } from 'lucide-react';
import { useLang } from '../i18n/LangContext';

const ContactForm = () => {
  const { t } = useLang();
  const tc = t.contact;

  const [preview, setPreview] = useState('');
  const [copied, setCopied] = useState(false);
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({ message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const MIN_LEN = 20;
  const MAX_LEN = 1000;

  const validateMessage = (val) => {
    if (!val || val.trim().length < MIN_LEN) return tc.min_error(MIN_LEN);
    if (val.trim().length > MAX_LEN) return tc.max_error(MAX_LEN);
    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const form = e.target;
    // Honeypot check
    const honeypot = form.querySelector('input[name="website"]').value;
    if (honeypot) {
      return; // Спам: тихо игнорируем
    }
    const data = Object.fromEntries(new FormData(form).entries());
    const msgError = validateMessage(data.message);
    if (msgError) {
      setErrors((prev) => ({ ...prev, message: msgError }));
      return;
    }
    if (!data.name || !data.email || !data.message) return;
    const bodyLines = [
      tc.email_body_title,
      tc.email_body_sep,
      `${tc.email_name}: ${data.name}`,
      `Email: ${data.email}`,
      '',
      `${tc.email_message}:`,
      data.message.trim()
    ];
    const body = bodyLines.join('\n');
    setPreview(body);
    const subject = encodeURIComponent(tc.email_subject(data.name));
    const mailBody = encodeURIComponent(body);
    setSubmitting(true);
    setSubmitted(false);
    // Небольшая задержка для анимации
    setTimeout(() => {
      window.location.href = `mailto:develop@shopprograms.com?subject=${subject}&body=${mailBody}`;
      setSubmitting(false);
      setSubmitted(true);
    }, 450);
  };

  const copyText = () => {
    if (!preview) return;
    navigator.clipboard.writeText(preview).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-white to-slate-50">
      <div className="container mx-auto px-6 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white/70 backdrop-blur-sm border border-slate-200 shadow-sm rounded-2xl p-8 md:p-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 tracking-tight">{tc.heading}</h2>
          <p className="text-slate-600 mb-8 max-w-2xl">{tc.subheading}</p>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{tc.name_label}</label>
                <div className="relative">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input name="name" type="text" required className="w-full rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-blue-500 py-[5px] px-[42px]" placeholder={tc.name_placeholder} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{tc.email_label}</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input name="email" type="email" required className="w-full rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-blue-500 py-[5px] px-[42px]" placeholder={tc.email_placeholder} />
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">{tc.message_label}</label>
                <div className="relative">
                  <MessageSquare size={16} className="absolute left-3 top-4 text-slate-400" />
                  <textarea
                    name="message"
                    rows={5}
                    required
                    value={message}
                    onChange={(e) => {
                      const v = e.target.value;
                      setMessage(v);
                      setErrors((prev) => ({ ...prev, message: validateMessage(v) }));
                    }}
                    placeholder={tc.message_placeholder}
                    className={`w-full rounded-lg border ${errors.message ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500'} py-[10px] px-[42px]`}
                  ></textarea>
                  {errors.message && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="absolute -bottom-5 left-0 text-xs text-red-600"
                    >
                      {errors.message}
                    </motion.div>
                  )}
                </div>
                <p className="mt-2 text-xs text-slate-500">{message.trim().length}/{MAX_LEN} {tc.chars_hint}</p>
              </div>
            </div>
            {/* Honeypot field */}
            <div className="hidden" aria-hidden="true">
              <input type="text" name="website" tabIndex="-1" autoComplete="off" placeholder="Leave empty" />
            </div>
            <div className="flex flex-wrap gap-4">
              <button
                type="submit"
                disabled={!!errors.message || submitting}
                className={`inline-flex items-center px-8 py-4 rounded-full font-semibold shadow transition relative overflow-hidden ${submitting ? 'bg-blue-500' : 'bg-blue-600 hover:bg-blue-700'} text-white disabled:opacity-60`}
              >
                <span className={`flex items-center ${submitting ? 'opacity-0' : 'opacity-100'} transition-opacity`}>
                  {tc.send_btn} <Send size={18} className="ml-2" />
                </span>
                {submitting && (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="w-5 h-5 border-2 border-white/70 border-t-transparent rounded-full animate-spin"></span>
                  </span>
                )}
              </button>
              <button type="button" onClick={copyText} disabled={!preview} className="px-6 py-3 rounded-full bg-white border border-slate-300 text-slate-700 font-medium shadow-sm hover:bg-slate-50 transition">
                {copied ? tc.copied : tc.copy_btn}
              </button>
            </div>
            {submitted && preview && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35 }}
                className="mt-4"
              >
                <label className="block text-sm font-medium text-slate-700 mb-1">{tc.preview_label}</label>
                <textarea readOnly value={preview} className="w-full rounded-lg border border-slate-200 text-xs p-3 h-40 bg-slate-100"></textarea>
                <p className="text-xs text-slate-500 mt-2">{tc.mail_if_not_open}</p>
              </motion.div>
            )}
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactForm;
